import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import { RefreshCw, Save, Globe, Check, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VISIBILITY_SECTIONS = [
  { key: 'compareStrip', label: 'Tools strip ("Every tool you\'re paying for…")' },
  { key: 'features', label: 'Feature cards grid' },
  { key: 'ai', label: 'AI chat demo section' },
  { key: 'comparison', label: 'CountBucks vs QuickBooks vs Xero table' },
  { key: 'testimonials', label: 'Testimonials' },
  { key: 'finalCta', label: 'Final green CTA panel' },
];

function Field({ label, children }) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      {children}
    </div>
  );
}

export default function OwnerAdminHomepage() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/owner-admin/homepage-content`, { credentials: 'include' });
      if (!res.ok) throw new Error('load failed');
      setContent(await res.json());
    } catch {
      toast.error('Failed to load homepage content');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const edit = (updater) => setContent((c) => {
    const next = JSON.parse(JSON.stringify(c));
    updater(next);
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/owner-admin/homepage-content`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(content),
      });
      if (!res.ok) throw new Error('save failed');
      toast.success('Homepage updated — changes are live immediately.');
    } catch {
      toast.error('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !content) {
    return (
      <div className="space-y-4 p-1" data-testid="admin-homepage-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const vis = content.sectionVisibility || {};

  return (
    <div className="space-y-6 pb-24" data-testid="admin-homepage-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold"><Globe className="h-6 w-6" /> Homepage CMS</h1>
          <p className="text-sm text-muted-foreground">Edit the public marketing homepage. Changes go live as soon as you save.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="admin-homepage-reload-btn">
            <RefreshCw className="mr-2 h-4 w-4" /> Reload
          </Button>
          <Button onClick={save} disabled={saving} data-testid="admin-homepage-save-btn">
            <Save className="mr-2 h-4 w-4" /> {saving ? 'Saving…' : 'Save & Publish'}
          </Button>
        </div>
      </div>

      {/* Hero */}
      <Card data-testid="admin-hero-card">
        <CardHeader>
          <CardTitle>Hero</CardTitle>
          <CardDescription>Top-of-page headline, copy and buttons.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <Field label="Badge label"><Input data-testid="hero-label-input" value={content.hero.label} onChange={(e) => edit((c) => { c.hero.label = e.target.value; })} /></Field>
          <Field label="Trust line"><Input data-testid="hero-trustline-input" value={content.hero.trustLine} onChange={(e) => edit((c) => { c.hero.trustLine = e.target.value; })} /></Field>
          <Field label="Headline line 1"><Input data-testid="hero-headline1-input" value={content.hero.headline1} onChange={(e) => edit((c) => { c.hero.headline1 = e.target.value; })} /></Field>
          <Field label="Headline line 2"><Input value={content.hero.headline2} onChange={(e) => edit((c) => { c.hero.headline2 = e.target.value; })} /></Field>
          <Field label="Headline line 3 (green)"><Input value={content.hero.headline3} onChange={(e) => edit((c) => { c.hero.headline3 = e.target.value; })} /></Field>
          <Field label="Benefit pills (comma-separated)"><Input value={content.hero.pills.join(', ')} onChange={(e) => edit((c) => { c.hero.pills = e.target.value.split(',').map((s) => s.trim()).filter(Boolean); })} /></Field>
          <div className="md:col-span-2">
            <Field label="Subtext"><Textarea rows={2} value={content.hero.subtext} onChange={(e) => edit((c) => { c.hero.subtext = e.target.value; })} /></Field>
          </div>
          <Field label="Primary button text"><Input value={content.hero.primaryCta} onChange={(e) => edit((c) => { c.hero.primaryCta = e.target.value; })} /></Field>
          <Field label="Secondary button text"><Input value={content.hero.secondaryCta} onChange={(e) => edit((c) => { c.hero.secondaryCta = e.target.value; })} /></Field>
        </CardContent>
      </Card>

      {/* Prices */}
      <Card data-testid="admin-prices-card">
        <CardHeader>
          <CardTitle>Prices — Old Way vs Smart Way</CardTitle>
          <CardDescription>The hero comparison card. Keep the old-way total in sync with the item prices.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            {content.oldWay.items.map((item, i) => (
              <div key={i} className="flex gap-2">
                <Input className="flex-1" value={item.name} data-testid={`oldway-name-${i}`} onChange={(e) => edit((c) => { c.oldWay.items[i].name = e.target.value; })} />
                <Input className="w-28" value={item.price} data-testid={`oldway-price-${i}`} onChange={(e) => edit((c) => { c.oldWay.items[i].price = e.target.value; })} />
              </div>
            ))}
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <Field label="Old-way total"><Input data-testid="oldway-total-input" value={content.oldWay.total} onChange={(e) => edit((c) => { c.oldWay.total = e.target.value; })} /></Field>
            <Field label="CountBucks price"><Input data-testid="smartway-total-input" value={content.smartWay.total} onChange={(e) => edit((c) => { c.smartWay.total = e.target.value; })} /></Field>
            <Field label="Price suffix"><Input value={content.smartWay.totalSuffix} onChange={(e) => edit((c) => { c.smartWay.totalSuffix = e.target.value; })} /></Field>
          </div>
          <Field label='Tools strip tagline'>
            <Input data-testid="strip-label-input" value={content.compareStrip.label} onChange={(e) => edit((c) => { c.compareStrip.label = e.target.value; })} />
          </Field>
        </CardContent>
      </Card>

      {/* Comparison table */}
      <Card data-testid="admin-comparison-card">
        <CardHeader>
          <CardTitle>Comparison table</CardTitle>
          <CardDescription>CountBucks vs {content.comparison.columns[1]} vs {content.comparison.columns[2]}. Toggle checkmarks per column.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            {content.comparison.columns.map((col, i) => (
              <Field key={i} label={`${col} price`}>
                <Input data-testid={`comparison-price-${i}`} value={content.comparison.prices[i]} onChange={(e) => edit((c) => { c.comparison.prices[i] = e.target.value; })} />
              </Field>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[480px] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="pb-2">Feature</th>
                  {content.comparison.columns.map((col) => <th key={col} className="pb-2 text-center">{col}</th>)}
                </tr>
              </thead>
              <tbody>
                {content.comparison.rows.map((row, ri) => (
                  <tr key={ri} className="border-t border-border">
                    <td className="py-1.5 pr-2">
                      <Input className="h-8" value={row.feature} onChange={(e) => edit((c) => { c.comparison.rows[ri].feature = e.target.value; })} />
                    </td>
                    {row.values.map((v, vi) => (
                      <td key={vi} className="py-1.5 text-center">
                        <button
                          type="button"
                          data-testid={`comparison-toggle-${ri}-${vi}`}
                          onClick={() => edit((c) => { c.comparison.rows[ri].values[vi] = !c.comparison.rows[ri].values[vi]; })}
                          className={`inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors ${v ? 'bg-emerald-500/20 text-emerald-500' : 'bg-muted text-muted-foreground'}`}
                          aria-label={`Toggle ${row.feature} for ${content.comparison.columns[vi]}`}
                        >
                          {v ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
                        </button>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="grid gap-4 md:grid-cols-[1fr_200px]">
            <Field label="Disclaimer"><Textarea rows={2} value={content.comparison.disclaimer} onChange={(e) => edit((c) => { c.comparison.disclaimer = e.target.value; })} /></Field>
            <Field label="Last verified (YYYY-MM-DD)"><Input data-testid="comparison-verified-input" value={content.comparison.lastVerified} onChange={(e) => edit((c) => { c.comparison.lastVerified = e.target.value; })} /></Field>
          </div>
        </CardContent>
      </Card>

      {/* Testimonials */}
      <Card data-testid="admin-testimonials-card">
        <CardHeader>
          <CardTitle>Testimonials</CardTitle>
          <CardDescription>Only publish quotes you have permission to use.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 lg:grid-cols-3">
          {content.testimonials.map((t, i) => (
            <div key={i} className="space-y-2 rounded-xl border border-border p-4">
              <Field label={`Quote ${i + 1}`}><Textarea rows={3} data-testid={`testimonial-quote-${i}`} value={t.quote} onChange={(e) => edit((c) => { c.testimonials[i].quote = e.target.value; })} /></Field>
              <Field label="Name"><Input data-testid={`testimonial-name-${i}`} value={t.name} onChange={(e) => edit((c) => { c.testimonials[i].name = e.target.value; })} /></Field>
              <Field label="Role"><Input value={t.role} onChange={(e) => edit((c) => { c.testimonials[i].role = e.target.value; })} /></Field>
              <Field label="Avatar URL"><Input value={t.avatar} onChange={(e) => edit((c) => { c.testimonials[i].avatar = e.target.value; })} /></Field>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Final CTA */}
      <Card data-testid="admin-cta-card">
        <CardHeader><CardTitle>Final CTA panel</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <Field label="Headline"><Input value={content.finalCta.headline} onChange={(e) => edit((c) => { c.finalCta.headline = e.target.value; })} /></Field>
          <Field label="Subtext"><Input value={content.finalCta.subtext} onChange={(e) => edit((c) => { c.finalCta.subtext = e.target.value; })} /></Field>
          <Field label="Button text"><Input value={content.finalCta.button} onChange={(e) => edit((c) => { c.finalCta.button = e.target.value; })} /></Field>
        </CardContent>
      </Card>

      {/* Section visibility */}
      <Card data-testid="admin-visibility-card">
        <CardHeader>
          <CardTitle>Section visibility</CardTitle>
          <CardDescription>Hide a section from the public homepage without deleting its content.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          {VISIBILITY_SECTIONS.map(({ key, label }) => (
            <div key={key} className="flex items-center justify-between rounded-xl border border-border px-4 py-3">
              <span className="text-sm">{label}</span>
              <Switch
                checked={vis[key] !== false}
                data-testid={`visibility-toggle-${key}`}
                onCheckedChange={(checked) => edit((c) => {
                  c.sectionVisibility = { ...(c.sectionVisibility || {}), [key]: checked };
                })}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button size="lg" onClick={save} disabled={saving} data-testid="admin-homepage-save-btn-bottom">
          <Save className="mr-2 h-4 w-4" /> {saving ? 'Saving…' : 'Save & Publish'}
        </Button>
      </div>
    </div>
  );
}
